import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { IAccountingsHistoryTag } from "./InvoiceTypes";
import { GET_ACCOUNTINGS_HISTORY } from "src/api/cred";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GetAccountingsHistory, GetAccountingsHistoryVariables } from "src/api/generated/GetAccountingsHistory";

export enum STATE {
    INIT,
    LOADING,
    LOADING_HISTORY_TAG_DATA
}

export const MAX_ACCOUNTING_HISTORY_TAGS_TO_SHOW = 3;

export class AccountingBookingTextStore {
    rootStore: RootStore;

    state: STATE = STATE.INIT;

    loadedAccountingsHistoryTagData: string[] = [];

    accountingsHistoryTagData: { [creditorId: string]: IAccountingsHistoryTag[] } = {};
    areAllAccountingsHistoryTagsDisplayed: { [creditorId: string]: boolean } = {};
    wasAccountingsHistoryTagSelected: { [accountingRowIndex: number]: boolean } = {};

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            state: observable,
            accountingsHistoryTagData: observable,
            areAllAccountingsHistoryTagsDisplayed: observable,
            loadedAccountingsHistoryTagData: observable,
            wasAccountingsHistoryTagSelected: observable,

            setState: action,
            loadAccountingsHistoryTagData: action,
            setAccountingsHistoryTagData: action,
            setAreAllAccountingsHistoryTagsDisplayed: action,
            setWasAccountingsHistoryTagSelected: action,

            displayedAccountingsHistoryTagsByCreditorId: computed
        });
    }

    setState = (state: STATE) => {
        this.state = state;
    };

    setAccountingsHistoryTagData = (accountingsHistoryTagData: IAccountingsHistoryTag[], creditorId: string) => {
        this.accountingsHistoryTagData[creditorId] = accountingsHistoryTagData;
    };

    setAreAllAccountingsHistoryTagsDisplayed = (creditorId: string, areAllAccountingsHistoryTagsDisplayed: boolean) => {
        this.areAllAccountingsHistoryTagsDisplayed[creditorId] = areAllAccountingsHistoryTagsDisplayed;
    };

    setWasAccountingsHistoryTagSelected = (accountingRowIndex: number, wasSelected: boolean) => {
        this.wasAccountingsHistoryTagSelected[accountingRowIndex] = wasSelected;
    };

    loadAccountingsHistoryTagData = async (creditorId: string) => {
        const hasAlreadyLoadedAccountingsHistoryTagData =
            this.loadedAccountingsHistoryTagData?.includes(creditorId) ?? false;

        if (
            !hasAlreadyLoadedAccountingsHistoryTagData ||
            (this.accountingsHistoryTagData[creditorId] && this.accountingsHistoryTagData[creditorId].length === 0)
        ) {
            this.loadedAccountingsHistoryTagData.push(creditorId);

            const { data }: { data: GetAccountingsHistory } = await apolloClientInstance.query<
                GetAccountingsHistory,
                GetAccountingsHistoryVariables
            >({
                query: GET_ACCOUNTINGS_HISTORY,
                variables: {
                    creditorId: creditorId
                }
            });

            const seenTexts = new Set<string>();

            const accountingsHistoryTagData: IAccountingsHistoryTag[] = data.accountings_history
                .filter((accountingsHistoryEntry) => {
                    if (seenTexts.has(accountingsHistoryEntry.text)) {
                        return false; // Skip duplicates
                    }
                    seenTexts.add(accountingsHistoryEntry.text);
                    return true;
                })
                .map((accountingsHistoryEntry) => {
                    return {
                        creditorId: accountingsHistoryEntry.creditorid,
                        text: accountingsHistoryEntry.text,
                        accountNumber: accountingsHistoryEntry.accountnumber,
                        system: accountingsHistoryEntry.system
                    };
                });

            this.setAccountingsHistoryTagData(accountingsHistoryTagData, creditorId);
        }
    };

    getAccountingsHistoryTagsCount = (creditorId: string): number => {
        if (this.accountingsHistoryTagData[creditorId]) {
            return this.accountingsHistoryTagData[creditorId].length;
        } else {
            return 0;
        }
    };

    hasMoreAccountingsHistoryTagsToDisplay = (creditorId: string | undefined): boolean => {
        if (creditorId) {
            return (
                this.getAccountingsHistoryTagsCount(creditorId) > MAX_ACCOUNTING_HISTORY_TAGS_TO_SHOW &&
                (this.areAllAccountingsHistoryTagsDisplayed[creditorId] === undefined ||
                    this.areAllAccountingsHistoryTagsDisplayed[creditorId] === false)
            );
        }

        return false;
    };

    get displayedAccountingsHistoryTagsByCreditorId(): { [creditorId: string]: IAccountingsHistoryTag[] } {
        let displayedAccountingsHistoryTagData: { [creditorId: string]: IAccountingsHistoryTag[] } = {};

        Object.entries(this.accountingsHistoryTagData).forEach(([creditorId, accountTags]) => {
            const allAccountingsHistoryTagsAreDisplayed =
                this.areAllAccountingsHistoryTagsDisplayed[creditorId] === true;

            if (
                allAccountingsHistoryTagsAreDisplayed ||
                this.getAccountingsHistoryTagsCount(creditorId) <= MAX_ACCOUNTING_HISTORY_TAGS_TO_SHOW
            ) {
                displayedAccountingsHistoryTagData[creditorId] = accountTags;
            } else if (this.hasMoreAccountingsHistoryTagsToDisplay(creditorId)) {
                displayedAccountingsHistoryTagData[creditorId] = accountTags.slice(
                    0,
                    MAX_ACCOUNTING_HISTORY_TAGS_TO_SHOW
                );
            }
        });

        return displayedAccountingsHistoryTagData;
    }
}
